import { Button, Select, Spinner, TextField } from '@cimpress/react-components';
import { useEffect, useState } from 'react';
import {
    CardBuilder,
    Content,
    Control,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    type Card,
    type SelectorOption,
} from '../Components';
import { useCancelableEffect } from '../tools';
import { postGenerateImage } from '../api';
import {
    useGenerateImageParams,
    MODEL_OPTIONS,
    CONTENT_TYPE_OPTIONS,
    ASPECT_RATIO_OPTIONS,
} from '../Components/GenerateImage/useGenerateImage';
import { MEDIA_TYPE_OPTIONS, IMAGE_COUNT_OPTIONS } from '../common/constants';

export const GenerateImage = () => {
    const {
        prompt,
        modelSelectedOption,
        aspectRatioSelectedOption,
        mediaTypeSelectedOption,
        contentTypeSelectedOption,
        imageCountSelectedOption,
        setPrompt,
        setModelSelectedOption,
        setAspectRatioSelectedOption,
        setMediaTypeSelectedOption,
        setContentTypeSelectedOption,
        setImageCountSelectedOption,
    } = useGenerateImageParams();

    const [resultRow, setResultRow] = useState(PreviewRow());
    const [isLoading, setIsLoading] = useState(false);
    const [callGenerate, setCallGenerate] = useState(false);
    const [showMediaType, setShowMediaType] = useState(true);
    const showAspectRatio = mediaTypeSelectedOption.value !== 'vector';

    useEffect(() => {
        if (contentTypeSelectedOption.value === 'photo') {
            setShowMediaType(false);
            setMediaTypeSelectedOption(MEDIA_TYPE_OPTIONS[0]);
        } else {
            setShowMediaType(true);
        }
    }, [contentTypeSelectedOption.value, setMediaTypeSelectedOption]);

    useCancelableEffect(
        (signal) => {
            (async () => {
                if (callGenerate && prompt) {
                    setIsLoading(true);

                    const imageResultCards: Card[] = [];

                    const resultImagePromises: Promise<string>[] = [];

                    const requestBody: {
                        prompt: string;
                        model?: string;
                        mediaType?: string;
                        contentType?: string;
                        aspectRatio?: string;
                    } = {
                        prompt,
                        model: modelSelectedOption.value || undefined,
                        mediaType: mediaTypeSelectedOption.value || undefined,
                        contentType: contentTypeSelectedOption.value || undefined,
                        aspectRatio: aspectRatioSelectedOption.value || undefined,
                    };

                    for (let i = 0; i < Number(imageCountSelectedOption.value); i++) {
                        const resultImagePromise = postGenerateImage({
                            body: requestBody,
                            signal,
                        });

                        resultImagePromises.push(resultImagePromise);
                    }

                    // Wait for all promises to settle
                    const resultImageUrls = await Promise.allSettled(resultImagePromises);

                    // Build the cards using resolved image URLs
                    for (const resultImageUrl of resultImageUrls) {
                        let imageResultCard;
                        if (resultImageUrl.status === 'fulfilled') {
                            imageResultCard = CardBuilder.ofType('imageUrl')
                                .withTitle(`Image`)
                                .withData({ imageUrl: resultImageUrl.value })
                                .build();
                        } else {
                            imageResultCard = CardBuilder.ofType('documentUrl')
                                .withTitle('Result Document')
                                .withLoader(async () => {
                                    throw resultImageUrl.reason;
                                })
                                .build();
                        }

                        imageResultCards.push(imageResultCard);
                        setResultRow(PreviewRow(...imageResultCards));
                    }

                    setIsLoading(false);
                    setCallGenerate(false);
                }
            })();
        },
        [
            callGenerate,
            prompt,
            modelSelectedOption.value,
            aspectRatioSelectedOption.value,
            mediaTypeSelectedOption.value,
            contentTypeSelectedOption.value,
            imageCountSelectedOption.value,
        ],
    );

    const onModelChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setModelSelectedOption(selected);
    };

    const onMediaTypeChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setMediaTypeSelectedOption(selected);
    };

    const onContentTypeChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setContentTypeSelectedOption(selected);
    };

    const onAspectRatioChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setAspectRatioSelectedOption(selected);
    };

    const onImageCountChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setImageCountSelectedOption(selected);
    };

    function onClickGenerate() {
        setCallGenerate(true);
    }

    return (
        <Page>
            <Page.Title>Generate Image</Page.Title>

            <Page.Controls>
                <Control>
                    <Control.Title>Prompt</Control.Title>
                    <TextField label="Prompt" onChange={(e) => setPrompt(e.target.value)} value={prompt ?? ''} />
                </Control>

                <Control>
                    <Control.Title>Model</Control.Title>
                    <Select
                        onChange={onModelChange}
                        value={modelSelectedOption}
                        options={MODEL_OPTIONS}
                        label="Model"
                    />
                </Control>
                <Control>
                    <Control.Title>Content Type</Control.Title>
                    <Select
                        onChange={onContentTypeChange}
                        value={contentTypeSelectedOption}
                        options={CONTENT_TYPE_OPTIONS}
                        label="Content Type"
                    />
                </Control>
                {showMediaType && (
                    <Control>
                        <Control.Title>Media Type</Control.Title>
                        <Select
                            onChange={onMediaTypeChange}
                            value={mediaTypeSelectedOption}
                            options={MEDIA_TYPE_OPTIONS}
                            label="Media Type"
                        />
                    </Control>
                )}
                {showAspectRatio ? (
                    <Control>
                        <Control.Title>Aspect Ratio</Control.Title>
                        <Select
                            onChange={onAspectRatioChange}
                            value={aspectRatioSelectedOption}
                            options={ASPECT_RATIO_OPTIONS}
                            label="Aspect Ratio"
                        />
                    </Control>
                ) : null}
                <Control>
                    <Control.Title>Image Count</Control.Title>
                    <Select
                        onChange={onImageCountChange}
                        value={imageCountSelectedOption}
                        options={IMAGE_COUNT_OPTIONS}
                        label="Image Count"
                    />
                </Control>
                <Control>
                    <Button onClick={onClickGenerate}>Generate</Button>
                </Control>
            </Page.Controls>

            <Page.Content>
                {isLoading ? (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <Spinner />
                        </Content.Section>
                    </Content.Card>
                ) : (
                    <PreviewSection
                        introduction="Fill in the prompt and click Generate to see generated results."
                        showIntro={!prompt}
                        rows={PreviewRows(resultRow)}
                    />
                )}
            </Page.Content>
        </Page>
    );
};

export default GenerateImage;
